import React from 'react';
import { useReducedMotion } from 'framer-motion';
import { AppMark } from '../data/site';

/**
 * The connections wall: two full-bleed rows that drift across the screen in
 * opposite directions ("connected today" left, "on request" right), pausing
 * on hover. Each row is rendered twice so the loop has no seam; the copy is
 * aria-hidden. Reduced motion lays both rows out as a static wrapped grid.
 *
 * `dashed` marks a catalogue app available on request (Composio) rather than
 * one the opsManager codebase already supports - per DESIGN.md section 7 it
 * renders as a dashed pill instead of a solid one, so the two claims stay
 * visually distinct.
 */
export interface MarqueeApp extends AppMark {
  dashed?: boolean;
}

/** Published mark colours, so each logo reads as itself on white. */
const BRAND: Record<string, string> = {
  google: '#4285F4',
  googleads: '#4285F4',
  googleanalytics: '#E37400',
  googlecalendar: '#1A73E8',
  gmail: '#EA4335',
  yelp: '#FF1A1A',
  instagram: '#E4405F',
  facebook: '#0866FF',
  meta: '#0866FF',
  tiktok: '#111111',
  square: '#3E4348',
  shopify: '#7AB55C',
  stripe: '#635BFF',
  quickbooks: '#2CA01C',
  intuit: '#236CFF',
  calendly: '#006BFF',
  slack: '#4A154B',
  notion: '#111111',
  airtable: '#18BFFF',
};

/** A palette for apps we have no vector mark for; keyed by first letter. */
const INITIAL: readonly string[] = ['#0f7f86', '#2f5fd6', '#e0902a', '#1f9a5a', '#d9a441', '#7c3aed'];

/**
 * Apps with an official full-colour logo at /brand/logos/<mark>.svg. A
 * 'mark' is a square icon shown beside the name; a 'wordmark' already says
 * the name, so it is shown alone. Anything not listed falls back to the
 * tinted monochrome glyph.
 */
const COLOR_LOGOS: Record<string, { kind: 'mark' | 'wordmark'; ext: 'svg' | 'webp' }> = {
  airtable: { kind: 'wordmark', ext: 'svg' },
  facebook: { kind: 'mark', ext: 'svg' },
  gmail: { kind: 'mark', ext: 'svg' },
  google: { kind: 'mark', ext: 'svg' },
  googleads: { kind: 'mark', ext: 'svg' },
  googlecalendar: { kind: 'mark', ext: 'svg' },
  instagram: { kind: 'mark', ext: 'svg' },
  intuit: { kind: 'wordmark', ext: 'svg' },
  meta: { kind: 'wordmark', ext: 'svg' },
  notion: { kind: 'mark', ext: 'svg' },
  quickbooks: { kind: 'wordmark', ext: 'svg' },
  shopify: { kind: 'wordmark', ext: 'svg' },
  slack: { kind: 'mark', ext: 'svg' },
  square: { kind: 'mark', ext: 'svg' },
  stripe: { kind: 'wordmark', ext: 'svg' },
  tiktok: { kind: 'mark', ext: 'svg' },
  yelp: { kind: 'wordmark', ext: 'svg' },
  calendly: { kind: 'mark', ext: 'webp' },
  signalwire: { kind: 'mark', ext: 'webp' },
  plaid: { kind: 'mark', ext: 'webp' },
  mercury: { kind: 'mark', ext: 'webp' },
  toast: { kind: 'mark', ext: 'webp' },
  clover: { kind: 'mark', ext: 'webp' },
  mindbody: { kind: 'mark', ext: 'webp' },
  vagaro: { kind: 'mark', ext: 'webp' },
  servicetitan: { kind: 'mark', ext: 'webp' },
  jobber: { kind: 'mark', ext: 'webp' },
  housecallpro: { kind: 'mark', ext: 'webp' },
};

const Card: React.FC<{ app: MarqueeApp; i: number }> = ({ app, i }) => {
  const logo = app.mark ? COLOR_LOGOS[app.mark] : undefined;
  const dashedClass = app.dashed ? ' capp--dashed' : '';
  if (app.mark && logo?.kind === 'wordmark') {
    return (
      <li className={`capp capp--word${dashedClass}`} title={app.name}>
        <img
          className="capp__word"
          src={`/brand/logos/${app.mark}.${logo.ext}`}
          alt={app.name}
          height={18}
          loading="lazy"
          decoding="async"
        />
      </li>
    );
  }
  if (app.mark && logo?.kind === 'mark') {
    return (
      <li className={`capp${dashedClass}`}>
        <img
          className="capp__img"
          src={`/brand/logos/${app.mark}.${logo.ext}`}
          alt=""
          width={26}
          height={26}
          loading="lazy"
          decoding="async"
        />
        <span className="capp__name">{app.name}</span>
      </li>
    );
  }
  if (app.mark) {
    const url = `url(/brand/apps/${app.mark}.svg)`;
    return (
      <li className={`capp${dashedClass}`}>
        <span
          className="capp__logo"
          aria-hidden="true"
          style={
            {
              background: BRAND[app.mark] ?? '#1b1e26',
              WebkitMaskImage: url,
              maskImage: url,
            } as React.CSSProperties
          }
        />
        <span className="capp__name">{app.name}</span>
      </li>
    );
  }
  return (
    <li className={`capp${dashedClass}`}>
      <span
        className="capp__initial"
        aria-hidden="true"
        style={{ background: INITIAL[i % INITIAL.length] }}
      >
        {app.name.charAt(0)}
      </span>
      <span className="capp__name">{app.name}</span>
    </li>
  );
};

const Row: React.FC<{ apps: readonly MarqueeApp[]; dir: 'left' | 'right'; still: boolean }> = ({
  apps,
  dir,
  still,
}) => (
  <div className={`cwall__row cwall__row--${dir}${still ? ' cwall__row--still' : ''}`}>
    <ul className="cwall__track">
      {apps.map((app, i) => (
        <Card app={app} i={i} key={app.name} />
      ))}
    </ul>
    {!still && (
      <ul className="cwall__track" aria-hidden="true">
        {apps.map((app, i) => (
          <Card app={app} i={i} key={app.name} />
        ))}
      </ul>
    )}
  </div>
);

interface Props {
  connectedToday: readonly MarqueeApp[];
  connectOnRequest: readonly MarqueeApp[];
}

const AppMarquee: React.FC<Props> = ({ connectedToday, connectOnRequest }) => {
  const still = Boolean(useReducedMotion());
  return (
    <div className="cwall" aria-label="Apps Arceus connects to">
      <div className="cwall__rows">
        <Row apps={connectedToday} dir="left" still={still} />
        <Row apps={connectOnRequest} dir="right" still={still} />
      </div>
    </div>
  );
};

export default React.memo(AppMarquee);
